import { Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';

import { GableDrawerService } from '../../../services/gable/gable-drawer.service';

/**
 * Left-edge resize handle for the Gable drawer.
 *
 * Capture mousedown on the handle, then listen at the window level for
 * mousemove/mouseup until the user lets go. New width = `window.innerWidth -
 * event.clientX`. Snap to 400 / 560 / 720 within 8px to honor the "magic"
 * stop points the design calls out.
 *
 * Persistence is handled by the service (clamp + localStorage).
 */
@Directive({
    selector: '[appGableDrawerResize]',
    standalone: true,
})
export class GableDrawerResizeDirective implements OnDestroy {
    private static readonly SNAP_POINTS = [400, 560, 720];
    private static readonly SNAP_THRESHOLD_PX = 8;

    private _dragging = false;
    private _onMove = (e: MouseEvent): void => this._handleMove(e);
    private _onUp = (): void => this._handleUp();

    constructor(
        private _host: ElementRef<HTMLElement>,
        private _drawer: GableDrawerService,
    ) {}

    @HostListener('mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        this._dragging = true;
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', this._onMove);
        window.addEventListener('mouseup', this._onUp);
    }

    ngOnDestroy(): void {
        this._cleanup();
    }

    private _handleMove(event: MouseEvent): void {
        if (!this._dragging) {
            return;
        }
        const raw = window.innerWidth - event.clientX;
        const snapped = this._maybeSnap(raw);
        this._drawer.setWidth(snapped);
    }

    private _handleUp(): void {
        this._dragging = false;
        this._cleanup();
    }

    private _cleanup(): void {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', this._onMove);
        window.removeEventListener('mouseup', this._onUp);
    }

    private _maybeSnap(px: number): number {
        for (const snap of GableDrawerResizeDirective.SNAP_POINTS) {
            if (Math.abs(px - snap) <= GableDrawerResizeDirective.SNAP_THRESHOLD_PX) {
                return snap;
            }
        }
        return px;
    }
}
